import { Loader2, SendHorizontal, X } from "lucide-react";
import { useState } from "react";

import { useAttachmentUploads } from "@/lib/attachments";
import type { RichDoc } from "@/lib/board";
import { cn } from "@/lib/utils";

import { RichCommentEditor } from "./RichCommentEditor";

/**
 * A self-contained rich comment composer (used for the main comment box, replies,
 * and inline edit). It owns its own draft state — text, rich doc, and pending
 * uploads — so the parent only supplies `onSubmit` and a `submitting` flag. To
 * reset after a successful post, the parent remounts via a changing `key` (the
 * reply/edit composers reset by simply unmounting when they close).
 */
export function InlineComposer({
  taskId,
  placeholder,
  submitting,
  onSubmit,
  onCancel,
  autoFocus,
  submitLabel,
  initialValue = null,
  initialText = "",
}: {
  taskId: string;
  placeholder: string;
  submitting: boolean;
  onSubmit: (payload: {
    body: string;
    rich: RichDoc | null;
    attachmentIds: string[];
  }) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  /** Labeled action button (e.g. "Save" for edit); omit for a paper-plane send. */
  submitLabel?: string;
  /** Initial editor content (rich doc or plain text) — for inline edit. */
  initialValue?: RichDoc | string | null;
  initialText?: string;
}) {
  const uploads = useAttachmentUploads(taskId);
  const [text, setText] = useState(initialText);
  const [rich, setRich] = useState<RichDoc | null>(
    initialValue && typeof initialValue === "object" ? initialValue : null,
  );

  const canSend =
    (text.trim().length > 0 || uploads.doneIds.length > 0) &&
    !uploads.uploading &&
    !submitting;

  function submit() {
    if (!canSend) return;
    onSubmit({ body: text.trim(), rich, attachmentIds: uploads.doneIds });
  }

  return (
    <RichCommentEditor
      value={initialValue}
      onChange={(doc, t) => {
        setRich(doc);
        setText(t);
      }}
      onSubmit={submit}
      onCancel={onCancel}
      placeholder={placeholder}
      autoFocus={autoFocus}
      pending={uploads.items}
      onAttach={uploads.add}
      onRemoveAttachment={uploads.remove}
      actions={
        <>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              aria-label="Cancel"
              className="flex size-7 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label={submitLabel ?? "Send"}
            className={cn(
              "flex cursor-pointer items-center justify-center gap-1 rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
              submitLabel
                ? "h-7 bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                : "size-7",
            )}
          >
            {submitLabel ? (
              submitting ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Saving…
                </>
              ) : (
                submitLabel
              )
            ) : submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <SendHorizontal className="size-4" />
            )}
          </button>
        </>
      }
    />
  );
}
